import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography } from "@mui/material";

interface DetailSection {
  title: string;
  items: DetailItem[];
}

interface DetailItem {
  label: string;
  value: string | number | React.ReactNode;
  isChip?: boolean;
  chipProps?: {
    color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    variant?: 'filled' | 'outlined';
    size?: 'small' | 'medium';
    icon?: React.ReactElement;
  };
}

interface ViewDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  sections: DetailSection[];
  specialContent?: {
    title: string;
    content: string | React.ReactNode;
  };
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}

const ViewDialog: React.FC<ViewDialogProps> = ({
  open,
  onClose,
  title,
  sections,
  specialContent,
  primaryAction,
  secondaryAction,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {sections.map((section, sectionIndex) => (
            <Box key={sectionIndex}>
              <Typography variant="subtitle2" color="text.secondary">
                {section.title}
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {section.items.map((item, itemIndex) => (
                  <Box 
                    key={itemIndex} 
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    <Typography variant="body2">{item.label}:</Typography>
                    {item.isChip ? (
                      <Chip
                        label={item.value}
                        size={item.chipProps?.size || "small"}
                        color={item.chipProps?.color || "default"}
                        variant={item.chipProps?.variant || "filled"}
                        icon={item.chipProps?.icon}
                      />
                    ) : (
                      <Typography 
                        variant="body2" 
                        fontWeight={typeof item.value === 'string' && item.value.includes('$') ? "bold" : "medium"}
                      >
                        {item.value}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          ))}

          {specialContent && (
            <Box>
              
              <Paper
                variant="outlined"
                sx={{ p: 1.5, mt: 1, backgroundColor: "#fafafa" }}
              >
                {specialContent.content}
              </Paper>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {secondaryAction && (
          <Button onClick={secondaryAction.onClick}>
            {secondaryAction.label}
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDialog;